import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Save, FileText, Download, MessageCircle, ShieldAlert, ShieldCheck, Trash2, Plus, Info, HelpCircle, Calculator, AlertTriangle } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import { api, PUBLIC_API_BASE } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { documentSchemas } from "@/config/documentSchemas";
import { getWhy, setDbOverrides } from "@/config/whyClauses";
import { useAuth } from "@/context/AuthContext";
import PersonaFormDialog from "@/components/entities/PersonaFormDialog";
import InmuebleFormDialog from "@/components/entities/InmuebleFormDialog";
import { AsesorPerfilField, CuentaField } from "@/components/perfil/WizardPerfilFields";
import { WhyCard } from "@/components/copiloto/WhyCard";
import { PersonaPicker } from "@/components/entities/PersonaPicker";
import { MONTO_FIELD } from "@/config/documentSchemas";

function montoDeOperacion(init, op, tipo) {
  const campo = MONTO_FIELD[tipo];
  if (campo && (init[campo] === undefined || init[campo] === "") && op.monto != null) init[campo] = op.monto;
  if (!init.moneda) init.moneda = op.moneda || "MXN";
}
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const OP_FIELDS = new Set(["_op_propietario_id", "_op_contraparte_id", "_op_inmueble_id"]);
const OP_FIELD_TO_KEY = {
  _op_propietario_id: "propietario_id",
  _op_contraparte_id: "contraparte_id",
  _op_inmueble_id: "inmueble_id",
};

// Evita crear dos documentos si el efecto corre dos veces (StrictMode / remontaje).
const _creating = new Map();

function FieldRow({ field, value, onChange, personas, inmuebles, onNewPersona, onNewInmueble, data, perfil, reloadPerfil }) {
  if (field.type === "asesor_perfil") {
    return <AsesorPerfilField field={field} value={value} onChange={onChange} perfil={perfil} reloadPerfil={reloadPerfil} />;
  }
  if (field.type === "cuenta") {
    const pid = data?.[field.personaField];
    const persona = personas.find((p) => p.id === pid);
    return <CuentaField field={field} value={value} onChange={onChange} personaId={pid} personaNombre={persona?.nombre || persona?.razon_social} />;
  }
  if (field.type === "note") {
    return (
      <div
        data-testid={`field-note-${field.name}`}
        className="rounded-xl bg-[#EDF5F3] border border-[#2A5C55]/25 px-4 py-3 text-sm text-[#1F4640] leading-relaxed"
      >
        <span className="font-semibold">Nota: </span>{field.text}
      </div>
    );
  }
  if (field.type === "text" || field.type === "number" || field.type === "date") {
    return (
      <input type={field.type} step={field.step} value={value ?? ""}
             onChange={(e) => onChange(field.name, e.target.value)}
             placeholder={field.placeholder}
             data-testid={`field-${field.name}`}
             className="w-full h-14 px-4 rounded-xl border border-[#CBD5E1] bg-white text-base outline-none focus:ring-2 focus:ring-[#1E3A5F]" />
    );
  }
  if (field.type === "mxn") {
    const readOnly = !!field.readOnly;
    return (
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
        <input type="number" step="0.01" value={value ?? ""}
               onChange={(e) => !readOnly && onChange(field.name, e.target.value)}
               readOnly={readOnly}
               tabIndex={readOnly ? -1 : undefined}
               data-testid={`field-${field.name}`}
               className={`w-full h-14 pl-8 pr-16 rounded-xl border text-base outline-none focus:ring-2 focus:ring-[#1E3A5F] ${readOnly ? "bg-slate-50 text-slate-600 border-[#E2DCD2] cursor-not-allowed" : "bg-white border-[#CBD5E1]"}`} />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{data?.moneda || "MXN"}</span>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <textarea value={value ?? ""} onChange={(e) => onChange(field.name, e.target.value)}
                data-testid={`field-${field.name}`} rows={4}
                className="w-full px-4 py-3 rounded-xl border border-[#CBD5E1] bg-white outline-none focus:ring-2 focus:ring-[#1E3A5F]" />
    );
  }
  if (field.type === "select") {
    return (
      <Select value={value || ""} onValueChange={(v) => onChange(field.name, v)}>
        <SelectTrigger className="h-14" data-testid={`field-${field.name}`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "radio") {
    return (
      <div className="space-y-2">
        {field.options.map((o) => (
          <button key={o.value} type="button" onClick={() => onChange(field.name, o.value)}
                  data-testid={`field-${field.name}-${o.value}`}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${value === o.value ? "border-[#1E3A5F] bg-[#E7EBF3] ring-2 ring-[#1E3A5F]/20" : "border-[#E2DCD2] hover:border-[#2A5C55]/40"}`}>
            <div className="font-semibold text-slate-900">{o.label}</div>
          </button>
        ))}
      </div>
    );
  }
  if (field.type === "checkboxGroup") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (v) => {
      const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
      onChange(field.name, next);
    };
    return (
      <div className="space-y-2">
        {field.options.map((o) => (
          <button key={o.value} type="button" onClick={() => toggle(o.value)}
                  data-testid={`field-${field.name}-${o.value}`}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${arr.includes(o.value) ? "border-[#2A5C55] bg-[#EDF5F3]" : "border-[#E2DCD2] hover:border-[#2A5C55]/40"}`}>
            <div className="font-semibold text-slate-900">{o.label}</div>
          </button>
        ))}
      </div>
    );
  }
  if (field.type === "persona") {
    return (
      <div className="space-y-2">
        <PersonaPicker value={value} onChange={(v) => onChange(field.name, v)} personas={personas} testid={`field-${field.name}`} />
        <button type="button" onClick={onNewPersona} data-testid={`field-${field.name}-new`}
                className="text-sm text-[#1E3A5F] font-semibold hover:underline inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> Registrar nueva persona
        </button>
      </div>
    );
  }
  if (field.type === "inmueble") {
    return (
      <div className="space-y-2">
        <Select value={value || ""} onValueChange={(v) => onChange(field.name, v)}>
          <SelectTrigger className="h-14" data-testid={`field-${field.name}`}><SelectValue placeholder="Elige un inmueble" /></SelectTrigger>
          <SelectContent>
            {inmuebles.map((i) => (
              <SelectItem key={i.id} value={i.id}>{i.direccion}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button type="button" onClick={onNewInmueble} data-testid={`field-${field.name}-new`}
                className="text-sm text-[#1E3A5F] font-semibold hover:underline inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> Registrar nuevo inmueble
        </button>
      </div>
    );
  }
  if (field.type === "tramos") {
    const arr = Array.isArray(value) ? value : [];
    const upd = (i, k, v) => {
      const next = arr.map((t, idx) => idx === i ? { ...t, [k]: v } : t);
      onChange(field.name, next);
    };
    const add = () => onChange(field.name, [...arr, {
      // id estable evita que React pierda foco/estado al eliminar tramos
      // intermedios (index-as-key rompe la asociación DOM↔item).
      id: (crypto.randomUUID ? crypto.randomUUID() : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
      concepto: "", monto: "", fecha: "",
    }]);
    const rm = (i) => onChange(field.name, arr.filter((_, idx) => idx !== i));
    return (
      <div className="space-y-3">
        {arr.map((t, i) => (
          <div key={t.id || `legacy-${i}`} className="p-3 rounded-xl bg-[#F3EFE6]/60 space-y-2 border border-[#E2DCD2]" data-testid={`tramo-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-600">Pago #{i + 1}</span>
              <button type="button" onClick={() => rm(i)} className="text-red-500 p-1" data-testid={`tramo-${i}-delete`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <input placeholder="Concepto (ej. Enganche / Firma escritura)" value={t.concepto || ""}
                   onChange={(e) => upd(i, "concepto", e.target.value)} data-testid={`tramo-${i}-concepto`}
                   className="w-full h-11 px-3 rounded-lg border border-[#CBD5E1] outline-none focus:ring-2 focus:ring-[#1E3A5F]" />
            <div className="grid grid-cols-2 gap-2">
              <input type="number" placeholder="Monto MXN" value={t.monto || ""}
                     onChange={(e) => upd(i, "monto", e.target.value)} data-testid={`tramo-${i}-monto`}
                     className="w-full h-11 px-3 rounded-lg border border-[#CBD5E1] outline-none focus:ring-2 focus:ring-[#1E3A5F]" />
              <input type="date" value={t.fecha || ""}
                     onChange={(e) => upd(i, "fecha", e.target.value)} data-testid={`tramo-${i}-fecha`}
                     className="w-full h-11 px-3 rounded-lg border border-[#CBD5E1] outline-none focus:ring-2 focus:ring-[#1E3A5F]" />
            </div>
          </div>
        ))}
        <button type="button" onClick={add} data-testid="tramo-add"
                className="w-full py-3 rounded-xl border-2 border-dashed border-[#CBD5E1] text-slate-600 hover:border-[#1E3A5F] hover:text-[#1E3A5F] font-semibold text-sm inline-flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" /> Agregar pago
        </button>
      </div>
    );
  }
  return null;
}

function PromesaMathBanner({ data, onAutofill }) {
  // Ejecuta el math check solo si hay precio_venta
  const precio = parseFloat(data.precio_venta || 0);
  if (!precio) return null;
  const arras = parseFloat(data.arras || 0);
  const parciales = Array.isArray(data.pagos_parciales_lista)
    ? data.pagos_parciales_lista.reduce((s, p) => s + parseFloat(p?.monto || 0), 0)
    : 0;
  const hayParciales = data.hay_pagos_parciales === "si";
  const remanente = precio - arras - (hayParciales ? parciales : 0);
  const v1Arras = parseFloat(data.vendedor_1_arras || 0);
  const v2Arras = parseFloat(data.vendedor_2_arras || 0);
  const v1Rem = parseFloat(data.vendedor_1_remanente || 0);
  const v2Rem = parseFloat(data.vendedor_2_remanente || 0);
  const hayV2 = data.hay_vendedor_2 === "si";
  const arrasSplit = v1Arras + (hayV2 ? v2Arras : 0);
  const remSplit = v1Rem + (hayV2 ? v2Rem : 0);
  const arrasOk = Math.abs(arrasSplit - arras) < 0.01 || arras === 0;
  const remOk = Math.abs(remSplit - remanente) < 0.01 || remanente === 0;
  const pct = precio ? ((arras / precio) * 100).toFixed(1) : "0.0";
  const problems = [];
  if (!arrasOk) problems.push(`Reparto de arras (${arrasSplit.toLocaleString("es-MX",{style:"currency",currency:"MXN"})}) ≠ Arras (${arras.toLocaleString("es-MX",{style:"currency",currency:"MXN"})})`);
  if (!remOk) problems.push(`Reparto del remanente (${remSplit.toLocaleString("es-MX",{style:"currency",currency:"MXN"})}) ≠ Remanente calculado (${remanente.toLocaleString("es-MX",{style:"currency",currency:"MXN"})})`);
  if (remanente < 0) problems.push("El precio es menor que arras + parciales — revisa montos");
  return (
    <div data-testid="math-banner"
         className={"mt-4 rounded-xl border p-3 text-sm " +
                    (problems.length ? "bg-[#FEF3C7] border-[#FCD34D] text-[#92400E]" : "bg-[#EDF5F3] border-[#2A5C55]/25 text-[#1F4640]")}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {problems.length
            ? <AlertTriangle className="w-4 h-4" data-testid="math-warn-icon" />
            : <Calculator className="w-4 h-4" data-testid="math-ok-icon" />}
          <span className="font-bold">
            {problems.length ? "Revisar montos" : "Todo cuadra"}
          </span>
          <span className="text-xs opacity-80">
            · Precio {precio.toLocaleString("es-MX",{style:"currency",currency:"MXN"})}
            · Arras {pct}%
            · Remanente {remanente.toLocaleString("es-MX",{style:"currency",currency:"MXN"})}
          </span>
        </div>
        {onAutofill && (
          <button type="button" onClick={onAutofill} data-testid="autofill-btn"
                  className="text-xs font-semibold underline hover:no-underline">
            Autocompletar arras 10% y reparto 50/50
          </button>
        )}
      </div>
      {problems.length > 0 && (
        <ul className="mt-2 text-xs list-disc pl-5 space-y-0.5" data-testid="math-issues">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
    </div>
  );
}

/**
 * Indicador inline de reparto por concepto (arras / remanente / parcial).
 * Se muestra debajo del campo del PRIMER vendedor mientras el asesor captura,
 * dando feedback en vivo antes de la validación final del banner.
 */
function RepartoIndicator({ concept, total, repartido }) {
  if (!(total > 0)) return null;
  const falta = Math.round((total - repartido) * 100) / 100;
  const cuadra = Math.abs(falta) < 0.01;
  const sobra = falta < -0.01;
  return (
    <div data-testid={`reparto-${concept}`}
         className={"mt-2 rounded-lg px-3 py-2 text-xs flex items-center gap-2 " +
                    (cuadra
                      ? "bg-[#EDF5F3] border border-[#2A5C55]/25 text-[#1F4640]"
                      : "bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E]")}>
        {cuadra
          ? <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
          : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
        <span>
          Repartido <b data-testid={`reparto-${concept}-repartido`}>{fmtMXN(repartido)}</b>
          {" "}de <b data-testid={`reparto-${concept}-total`}>{fmtMXN(total)}</b>
          {!cuadra && (
            <> · {sobra ? "Sobra" : "Falta"} <b data-testid={`reparto-${concept}-falta`}>{fmtMXN(Math.abs(falta))}</b></>
          )}
        </span>
    </div>
  );
}

export default function WizardPage() {
  const nav = useNavigate();
  const { id: opIdParam, tipo: tipoParam, did } = useParams();
  const { user } = useAuth();
  const isAdminUser = user?.role === "owner";
  const [doc, setDoc] = useState(null);
  // Cargar overrides del banco desde el backend (Panel Admin). Defaults viven
  // en /config/whyClauses.js; los overrides sobreescriben por id.
  useEffect(() => {
    api.get("/why-entries").then((r) => setDbOverrides(r.data)).catch(() => {});
  }, []);
  const [opInfo, setOpInfo] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [inmuebles, setInmuebles] = useState([]);
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [inmuebleOpen, setInmuebleOpen] = useState(false);
  const [pendingField, setPendingField] = useState(null);
  const [preview, setPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [perfil, setPerfil] = useState(null);
  const reloadPerfil = useCallback(async () => {
    const { data: p } = await api.get("/perfil");
    setPerfil(p);
  }, []);
  useEffect(() => { reloadPerfil().catch(() => {}); }, [reloadPerfil]);

  const schema = doc ? documentSchemas[doc.tipo] : (tipoParam ? documentSchemas[tipoParam] : null);
  const opId = doc?.operacion_id || opIdParam;

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [pRes, iRes] = await Promise.all([api.get("/personas"), api.get("/inmuebles")]);
        setPersonas(pRes.data); setInmuebles(iRes.data);

        if (did) {
          const { data: d } = await api.get(`/documentos/${did}`);
          setDoc(d); setData(d.data || {});
          const opRes = await api.get(`/operaciones/${d.operacion_id}`);
          setOpInfo(opRes.data);
          const init = { ...(d.data || {}) };
          if (opRes.data.operacion.propietario_id) init._op_propietario_id = opRes.data.operacion.propietario_id;
          if (opRes.data.operacion.contraparte_id) init._op_contraparte_id = opRes.data.operacion.contraparte_id;
          if (opRes.data.operacion.inmueble_id) init._op_inmueble_id = opRes.data.operacion.inmueble_id;
          montoDeOperacion(init, opRes.data.operacion, d.tipo);
          setData(init);
        } else if (opIdParam && tipoParam) {
          const opRes = await api.get(`/operaciones/${opIdParam}`);
          setOpInfo(opRes.data);
          const sch = documentSchemas[tipoParam];
          const key = `${opIdParam}:${tipoParam}`;
          if (!_creating.has(key)) {
            _creating.set(key, api.post("/documentos", {
              operacion_id: opIdParam, tipo: tipoParam, titulo: sch.initialTitle, data: {},
            }));
            setTimeout(() => _creating.delete(key), 5000);
          }
          const { data: created } = await _creating.get(key);
          setDoc(created);
          const init = {};
          if (opRes.data.operacion.propietario_id) init._op_propietario_id = opRes.data.operacion.propietario_id;
          if (opRes.data.operacion.contraparte_id) init._op_contraparte_id = opRes.data.operacion.contraparte_id;
          if (opRes.data.operacion.inmueble_id) init._op_inmueble_id = opRes.data.operacion.inmueble_id;
          montoDeOperacion(init, opRes.data.operacion, tipoParam);
          setData(init);
          window.history.replaceState(null, "", `/app/documentos/${created.id}`);
        }
      } catch (e) {
        toast.error("Error al cargar");
      }
    })();
  }, [did, opIdParam, tipoParam]);

  // Auto-save
  useEffect(() => {
    if (!doc) return;
    const t = setTimeout(async () => {
      try {
        setSaving(true);
        const clean = Object.fromEntries(Object.entries(data).filter(([k]) => !OP_FIELDS.has(k)));
        await api.put(`/documentos/${doc.id}`, { data: clean });
        // Sync op fields
        const opUpd = {};
        Object.entries(data).forEach(([k, v]) => {
          if (OP_FIELDS.has(k) && v) opUpd[OP_FIELD_TO_KEY[k]] = v;
        });
        if (Object.keys(opUpd).length) await api.put(`/operaciones/${opId}`, opUpd);
      } catch (err) {
        // Autosave silencioso: en dev sí loggeamos porque sin esto es imposible
        // debuggear fallos intermitentes del guardado; en prod queda mudo.
        if (process.env.NODE_ENV !== "production") {
          console.warn("[autosave] fallo al guardar cambios del wizard", err?.response?.data || err?.message || err);
        }
      } finally { setSaving(false); }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Auto-cálculo del reparto para el ÚLTIMO vendedor (Promesa).
  // Regla: si hay V2, su porción = total − porción de V1. Se recalcula en cada
  // cambio de arras / V1_arras / precio / parciales / V1_remanente para que
  // NUNCA quede desfasado del banner de validación final.
  useEffect(() => {
    if (doc?.tipo !== "promesa_compraventa") return;
    if (data.hay_vendedor_2 !== "si") return;
    const arras = parseFloat(data.arras || 0);
    const v1Arras = parseFloat(data.vendedor_1_arras || 0);
    const precio = parseFloat(data.precio_venta || 0);
    const parciales = Array.isArray(data.pagos_parciales_lista)
      ? data.pagos_parciales_lista.reduce((s, p) => s + parseFloat(p?.monto || 0), 0) : 0;
    const hayParciales = data.hay_pagos_parciales === "si";
    const rem = precio - arras - (hayParciales ? parciales : 0);
    const v1Rem = parseFloat(data.vendedor_1_remanente || 0);
    const nextV2Arras = Math.max(0, Math.round((arras - v1Arras) * 100) / 100);
    const nextV2Rem = Math.max(0, Math.round((rem - v1Rem) * 100) / 100);
    const curV2Arras = parseFloat(data.vendedor_2_arras || 0);
    const curV2Rem = parseFloat(data.vendedor_2_remanente || 0);
    if (Math.abs(curV2Arras - nextV2Arras) > 0.001 || Math.abs(curV2Rem - nextV2Rem) > 0.001) {
      setData((p) => ({ ...p, vendedor_2_arras: nextV2Arras, vendedor_2_remanente: nextV2Rem }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.tipo, data.hay_vendedor_2, data.arras, data.vendedor_1_arras,
      data.precio_venta, data.hay_pagos_parciales, data.pagos_parciales_lista,
      data.vendedor_1_remanente]);

  const setField = useCallback((name, val) => setData((p) => (p[name] === val ? p : { ...p, [name]: val })), []);

  if (!doc || !schema) {
    return <div className="min-h-screen"><AppHeader /><div className="py-20 text-center text-slate-500">Cargando…</div></div>;
  }

  const steps = schema.steps;
  const step = steps[stepIdx];
  const progress = Math.round(((stepIdx + 1) / steps.length) * 100);

  const canProceed = () => {
    for (const f of step.fields) {
      if (f.showIf && !f.showIf(data)) continue;
      if (f.type === "asesor_perfil" && f.required && !perfil?.completo) return false;
      if (f.required) {
        const v = data[f.name];
        if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!canProceed()) {
      const needsPerfil = step.fields.some((f) => f.type === "asesor_perfil" && f.required) && !perfil?.completo;
      return toast.error(needsPerfil ? "Completa tu perfil para continuar" : "Completa los campos requeridos");
    }
    if (stepIdx < steps.length - 1) setStepIdx(stepIdx + 1);
    else openPreview();
  };
  const prev = () => stepIdx > 0 ? setStepIdx(stepIdx - 1) : nav(`/app/operaciones/${opId}`);

  const errorDoc = async (e, fallback) => {
    let msg = fallback;
    try { msg = JSON.parse(await e.response.data.text()).detail || fallback; } catch { /* sin detalle */ }
    toast.error(msg);
  };

  const openPreview = async () => {
    try {
      setSaving(true);
      const clean = Object.fromEntries(Object.entries(data).filter(([k]) => !OP_FIELDS.has(k)));
      await api.put(`/documentos/${doc.id}`, { data: clean, estado: "listo" });
      const opUpd = {};
      Object.entries(data).forEach(([k, v]) => { if (OP_FIELDS.has(k) && v) opUpd[OP_FIELD_TO_KEY[k]] = v; });
      if (Object.keys(opUpd).length) await api.put(`/operaciones/${opId}`, opUpd);

      const resp = await api.get(`/documentos/${doc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setPdfUrl(url);
      setPreview(true);
    } catch (e) {
      await errorDoc(e, "Error generando vista previa");
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/documentos/${doc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${doc.titulo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { await errorDoc(e, "No se pudo descargar el PDF"); }
  };

  const downloadDocx = async () => {
    try {
      const resp = await api.get(`/documentos/${doc.id}/docx`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${doc.titulo}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Word descargado — editable en Word / Google Docs / Pages");
    } catch (e) { await errorDoc(e, "No se pudo descargar el Word"); }
  };

  const solicitarValidacion = async () => {
    await api.post(`/documentos/${doc.id}/validacion/solicitar`, { notas: "" });
    toast.success("Validación experta solicitada");
  };

  // --- Share token helpers ---
  const shareStatus = (() => {
    if (!doc) return { state: "legacy", label: "" };
    if (doc.share_token_revoked_at) return { state: "revoked", label: "Enlace revocado" };
    const exp = doc.share_token_expires_at;
    if (!exp) return { state: "legacy", label: "" };
    const expDt = new Date(exp);
    if (Date.now() > expDt.getTime()) return { state: "expired", label: "Enlace expirado" };
    const fmt = expDt.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
    return { state: "active", label: `Expira el ${fmt}`, expDt };
  })();
  const canShare = shareStatus.state === "active" || shareStatus.state === "legacy";

  const expiryLine = (() => {
    // Avisa al cliente la fecha de vencimiento para que sepa cuánto tiempo tiene.
    if (!shareStatus.expDt) return "";
    const fmt = shareStatus.expDt.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
    return ` Vence el ${fmt}.`;
  })();

  const shareWA = () => {
    if (!canShare) return toast.error("El enlace ya no está activo. Genera uno nuevo.");
    const publicUrl = `${PUBLIC_API_BASE}/public/documentos/${doc.share_token}/pdf`;
    const msg = `Hola, te comparto el documento "${doc.titulo}". Puedes verlo o descargarlo aquí: ${publicUrl}.${expiryLine}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const shareWADocx = () => {
    if (!canShare) return toast.error("El enlace ya no está activo. Genera uno nuevo.");
    const publicUrl = `${PUBLIC_API_BASE}/public/documentos/${doc.share_token}/docx`;
    const msg = `Hola, te comparto la versión editable en Word del documento "${doc.titulo}": ${publicUrl}.${expiryLine}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const revokeShare = async () => {
    if (!window.confirm("¿Revocar este enlace? Cualquier persona que ya lo tenga dejará de poder abrirlo.")) return;
    try {
      const { data } = await api.post(`/documentos/${doc.id}/share/revoke`);
      setDoc(data);
      toast.success("Enlace revocado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo revocar");
    }
  };

  const rotateShare = async () => {
    try {
      const { data } = await api.post(`/documentos/${doc.id}/share/rotate`);
      setDoc(data);
      toast.success("Enlace nuevo generado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar un enlace nuevo");
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => nav(`/app/operaciones/${opId}`)} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5 mb-4" data-testid="wizard-back">
          <ArrowLeft className="w-4 h-4" /> Volver a la operación
        </button>

        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
            <span className="font-semibold">Paso {stepIdx + 1} de {steps.length}</span>
            <span>{saving ? "Guardando…" : "Guardado"}</span>
          </div>
          <Progress value={progress} className="h-2" data-testid="wizard-progress" />
          <div className="mt-2 text-xs text-slate-500 font-semibold uppercase tracking-widest">{schema.label}</div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.22 }}>
            <div className="bm-card p-6 sm:p-8">
              <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-slate-900" data-testid="wizard-step-title">{step.title}</h1>
              {step.help && (
                <p className="text-sm text-slate-600 mt-2 flex items-start gap-2">
                  <Info className="w-4 h-4 text-[#2A5C55] shrink-0 mt-0.5" /> {step.help}
                </p>
              )}
              <div className="mt-6 space-y-5">
                {doc?.tipo === "promesa_compraventa" && (
                  <PromesaMathBanner
                    data={data}
                    onAutofill={() => {
                      const precio = parseFloat(data.precio_venta || 0);
                      if (!precio) { toast.info("Ingresa primero el precio de venta"); return; }
                      const arras = Math.round(precio * 0.10 * 100) / 100;
                      const hayV2 = data.hay_vendedor_2 === "si";
                      const parciales = Array.isArray(data.pagos_parciales_lista)
                        ? data.pagos_parciales_lista.reduce((s, p) => s + parseFloat(p?.monto || 0), 0) : 0;
                      const rem = precio - arras - (data.hay_pagos_parciales === "si" ? parciales : 0);
                      const patch = {
                        arras,
                        vendedor_1_arras: hayV2 ? Math.round(arras / 2 * 100) / 100 : arras,
                        vendedor_1_remanente: hayV2 ? Math.round(rem / 2 * 100) / 100 : rem,
                      };
                      if (hayV2) {
                        patch.vendedor_2_arras = arras - patch.vendedor_1_arras;
                        patch.vendedor_2_remanente = rem - patch.vendedor_1_remanente;
                      }
                      Object.entries(patch).forEach(([k, v]) => setField(k, v));
                      toast.success("Arras 10% y reparto autocompletados");
                    }}
                  />
                )}
                {step.fields.map((f) => {
                  if (f.showIf && !f.showIf(data)) return null;
                  const why = getWhy(f.name, doc?.tipo);
                  return (
                    <div key={f.name}>
                      {f.label && (
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <label className="text-sm font-semibold text-slate-700">
                            {f.label}{f.required && <span className="text-red-500">*</span>}
                          </label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                data-testid={`why-${f.name}`}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-[#2A5C55] hover:text-[#1F4640] rounded-full px-2 py-1 hover:bg-[#EDF5F3] transition-colors"
                              >
                                <HelpCircle className="w-3.5 h-3.5" />
                                ¿Por qué esta cláusula?
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="end"
                              sideOffset={6}
                              className="w-[320px] sm:w-[400px] p-4 rounded-2xl border-[#E2DCD2] shadow-xl"
                              data-testid={`why-popover-${f.name}`}
                            >
                              <WhyCard why={why} name={f.name} isOwner={isAdminUser} />
                            </PopoverContent>
                          </Popover>
                        </div>
                      )}
                      <FieldRow
                        field={f}
                        value={data[f.name]}
                        onChange={setField}
                        personas={personas}
                        inmuebles={inmuebles}
                        onNewPersona={() => { setPendingField(f.name); setPersonaOpen(true); }}
                        onNewInmueble={() => { setPendingField(f.name); setInmuebleOpen(true); }}
                        data={data}
                        perfil={perfil}
                        reloadPerfil={reloadPerfil}
                      />
                      {f.hint && (
                        <p data-testid={`field-hint-${f.name}`} className="text-xs text-slate-500 mt-1.5 flex items-start gap-1.5">
                          <Info className="w-3.5 h-3.5 text-[#2A5C55] shrink-0 mt-0.5" /> {f.hint}
                        </p>
                      )}
                      {/* Indicador inline de reparto — sólo si hay V2 y estamos en el campo del V1. */}
                      {doc?.tipo === "promesa_compraventa" && data.hay_vendedor_2 === "si" && f.name === "vendedor_1_arras" && (
                        <RepartoIndicator
                          concept="arras"
                          total={parseFloat(data.arras || 0)}
                          repartido={parseFloat(data.vendedor_1_arras || 0) + parseFloat(data.vendedor_2_arras || 0)} />
                      )}
                      {doc?.tipo === "promesa_compraventa" && data.hay_vendedor_2 === "si" && f.name === "vendedor_1_remanente" && (() => {
                        const precio = parseFloat(data.precio_venta || 0);
                        const arrasT = parseFloat(data.arras || 0);
                        const parcT = Array.isArray(data.pagos_parciales_lista)
                          ? data.pagos_parciales_lista.reduce((s, p) => s + parseFloat(p?.monto || 0), 0) : 0;
                        const remTotal = precio - arrasT - (data.hay_pagos_parciales === "si" ? parcT : 0);
                        return (
                          <RepartoIndicator
                            concept="remanente"
                            total={remTotal}
                            repartido={parseFloat(data.vendedor_1_remanente || 0) + parseFloat(data.vendedor_2_remanente || 0)} />
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-6 gap-3">
          <button onClick={prev} className="bm-btn-secondary" data-testid="wizard-prev">
            <ArrowLeft className="w-4 h-4" /> {stepIdx === 0 ? "Cancelar" : "Anterior"}
          </button>
          <button onClick={next} className="bm-btn-primary flex-1 sm:flex-initial" data-testid="wizard-next">
            {stepIdx === steps.length - 1 ? <>Ver vista previa <FileText className="w-4 h-4" /></> : <>Siguiente <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </main>

      <PersonaFormDialog open={personaOpen} onOpenChange={setPersonaOpen}
                         onSaved={(p) => { setPersonas((prev) => [p, ...prev]); if (pendingField) setField(pendingField, p.id); }} />
      <InmuebleFormDialog open={inmuebleOpen} onOpenChange={setInmuebleOpen}
                          onSaved={(i) => { setInmuebles((prev) => [i, ...prev]); if (pendingField) setField(pendingField, i.id); }} />

      {preview && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" data-testid="preview-modal">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-4 border-b border-[#E2DCD2] flex items-center justify-between gap-3">
              <div>
                <h3 className="font-heading font-bold text-slate-900">{doc.titulo}</h3>
                <p className="text-xs text-slate-500">{schema.label}</p>
              </div>
              <button onClick={() => setPreview(false)} className="text-slate-500 hover:text-slate-900 px-3 py-1" data-testid="preview-close">Cerrar</button>
            </div>
            {/* Validation Banner — UI only, NOT inside PDF */}
            <div className="p-4 bg-[#FFFBEB] border-b border-[#FCD34D]" data-testid="validation-banner">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-[#D97706] shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#92400E]">Se recomienda validación profesional antes de su firma</div>
                  <p className="text-xs text-[#92400E]/85 mt-0.5">
                    Este documento se generó a partir de tus respuestas. Antes de firmarlo,
                    considera solicitar una revisión jurídica o fiscal antes de firmarlo.
                  </p>
                </div>
                {doc.validacion_estado === "validada" ? (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold bg-emerald-50 px-2 py-1 rounded-full">
                    <ShieldCheck className="w-3.5 h-3.5" /> Validado
                  </span>
                ) : doc.validacion_estado === "solicitada" ? (
                  <span className="text-xs font-semibold text-amber-700">Revisión solicitada</span>
                ) : (
                  <button onClick={solicitarValidacion} className="bm-btn-secondary text-xs min-h-[36px] px-3" data-testid="request-validation-btn">
                    Solicitar revisión
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-slate-100">
              <iframe src={pdfUrl} title="preview" className="w-full h-full" data-testid="preview-iframe" />
            </div>
            <div className="p-4 border-t border-[#E2DCD2] grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={downloadPdf} className="bm-btn-primary" data-testid="download-pdf-btn">
                <Download className="w-4 h-4" /> Descargar PDF
              </button>
              <button onClick={downloadDocx} className="bm-btn-secondary" data-testid="download-docx-btn">
                <FileText className="w-4 h-4" /> Descargar en Word (editable)
              </button>
              <button onClick={shareWA} disabled={!canShare} className="bm-btn-secondary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="share-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> Compartir PDF por WhatsApp
              </button>
              <button onClick={shareWADocx} disabled={!canShare} className="bm-btn-secondary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="share-whatsapp-docx-btn">
                <MessageCircle className="w-4 h-4" /> Compartir Word por WhatsApp
              </button>
              <p className="col-span-1 sm:col-span-2 text-xs text-slate-500" data-testid="google-docs-note">
                ¿Usas Google Docs? Sube este archivo a tu Drive y ábrelo con Google Docs.
              </p>
              <div className="col-span-1 sm:col-span-2 mt-1 rounded-xl bg-[#FBF9F5] border border-[#E2DCD2] px-3 py-2.5 flex flex-wrap items-center justify-between gap-2" data-testid="share-status-row">
                <div className="text-xs text-slate-700">
                  <span className="font-semibold">Enlace público:</span>{" "}
                  {shareStatus.state === "active" && <span className="text-[#047857]" data-testid="share-status">{shareStatus.label}</span>}
                  {shareStatus.state === "expired" && <span className="text-[#92400E]" data-testid="share-status">Expirado</span>}
                  {shareStatus.state === "revoked" && <span className="text-[#92400E]" data-testid="share-status">Revocado</span>}
                  {shareStatus.state === "legacy" && <span className="text-slate-500" data-testid="share-status">Sin caducidad registrada</span>}
                </div>
                <div className="flex items-center gap-2">
                  {canShare && (
                    <button onClick={revokeShare} className="text-xs font-semibold text-[#92400E] hover:underline" data-testid="revoke-share-btn">
                      Revocar
                    </button>
                  )}
                  <button onClick={rotateShare} className="text-xs font-semibold text-[#1E3A5F] hover:underline" data-testid="rotate-share-btn">
                    Generar enlace nuevo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
